import Papa from 'papaparse';
import type { ImportFieldNode } from '@erp/rpc';
import { parseSampleRows } from 'src/components/schema-definitions/sampleRowsParser';
import { parseSampleRecords } from 'src/components/schema-definitions/samplePayloadParser';

/**
 * Suggests schema fields from an asset's actual contents.
 *
 * A data asset can be arbitrarily large, so this never downloads the whole
 * file: it asks for a byte range and stops reading at {@link MAX_SAMPLE_BYTES}
 * even if the server ignores the range header.
 */

/** Bytes pulled from the asset. Comfortably more than SAMPLE_ROW_LIMIT rows. */
export const MAX_SAMPLE_BYTES = 512 * 1024;

/**
 * The parsed sample behind an inference, so callers can reason about the values
 * and not just the field list — the key-uniqueness check counts distinct keys
 * over these rows. Null for JSON, where records are not a flat row grid.
 */
export interface SampledRows {
  header: string[];
  rows: string[][];
  /** True when the read stopped at the byte cap, short of the whole file. */
  truncated: boolean;
}

export type SampleFieldsResult =
  | {
      ok: true;
      fields: ImportFieldNode[];
      warnings: string[];
      sample: SampledRows | null;
    }
  | { ok: false; error: string };

/**
 * Reads at most `maxBytes` from a URL, cancelling the response as soon as the
 * cap is reached. Returns whether the read stopped short of the end, since a
 * truncated CSV has a partial final row and truncated JSON can't be parsed.
 */
async function readCapped(
  url: string,
  maxBytes: number,
): Promise<{ text: string; truncated: boolean }> {
  const response = await fetch(url, {
    headers: { Range: `bytes=0-${maxBytes - 1}` },
  });
  if (!response.ok && response.status !== 206) {
    throw new Error(`HTTP ${response.status}`);
  }

  // A 206 does NOT imply the file was cut short — a range covering the whole
  // file still comes back as 206. Content-Range carries the real total, so use
  // that; without it, fall back to whether the read actually hit the cap.
  const totalBytes = parseTotalBytes(response.headers.get('Content-Range'));
  const knownTruncated = totalBytes !== null && totalBytes > maxBytes;

  if (!response.body) {
    const text = await response.text();
    return {
      text: text.slice(0, maxBytes),
      truncated: knownTruncated || text.length > maxBytes,
    };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let text = '';
  let bytes = 0;
  let hitCap = false;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      text += decoder.decode(value, { stream: true });
      if (bytes >= maxBytes) {
        hitCap = true;
        break;
      }
    }
  } finally {
    await reader.cancel().catch(() => {
      // The body is being abandoned on purpose; a cancel failure is not useful.
    });
  }

  return { text, truncated: knownTruncated || hitCap };
}

/** Total size from a `Content-Range: bytes 0-511/25076` header, if stated. */
function parseTotalBytes(contentRange: string | null): number | null {
  if (!contentRange) return null;
  const total = contentRange.split('/')[1]?.trim();
  if (!total || total === '*') return null;
  const parsed = Number(total);
  return Number.isFinite(parsed) ? parsed : null;
}

export async function sampleFieldsFromAsset(params: {
  downloadUrl: string;
  format: 'csv' | 'json';
}): Promise<SampleFieldsResult> {
  const { downloadUrl, format } = params;

  let text: string;
  let truncated: boolean;
  try {
    const read = await readCapped(downloadUrl, MAX_SAMPLE_BYTES);
    text = read.text;
    truncated = read.truncated;
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof Error
          ? `Could not read the file: ${error.message}`
          : 'Could not read the file',
    };
  }

  if (format === 'json') {
    if (truncated) {
      return {
        ok: false,
        error:
          'This JSON file is too large to sample. Suggesting a schema needs to parse the whole document.',
      };
    }
    // Handles a top-level array of records as well as a single object, unioning
    // keys across elements — the JSON equivalent of sampling several CSV rows.
    const result = parseSampleRecords(text);
    // JSON records are not a flat grid, so there is no row sample to hand back.
    return result.ok ? { ...result, warnings: [], sample: null } : result;
  }

  // Every row within the byte cap is parsed, not just the typing sample:
  // duplicate columns are compared across all of them, and real files have been
  // seen to diverge in a single row well past the first couple hundred.
  const parsed = Papa.parse<string[]>(text, { skipEmptyLines: 'greedy' });

  const rows = parsed.data;
  if (rows.length === 0) {
    return { ok: false, error: 'The file appears to be empty.' };
  }

  const [header, ...dataRows] = rows;

  // A capped read almost certainly cut the last line mid-row, which would
  // mistype a column or fake a duplicate-column mismatch.
  if (truncated && dataRows.length > 1) {
    dataRows.pop();
  }

  const result = parseSampleRows(header, dataRows);
  const sample: SampledRows = {
    header: header.map((name) => (name ?? '').trim()),
    rows: dataRows,
    truncated,
  };
  if (result.ok && truncated) {
    result.warnings.push(
      `Only the first ${Math.floor(
        MAX_SAMPLE_BYTES / 1024,
      )} KB of this file was sampled, so types and duplicate-column checks are a best guess. Verifying checks the whole file.`,
    );
  }
  return result.ok ? { ...result, sample } : result;
}
